import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import CrossService from "../puzzles/crossword/CrossService";
import GridService from "../puzzles/grid/GridService";
import JigsawPlayService from "../puzzles/jigsaw/JigsawPlayService";
import SlidingPlayService from "../puzzles/sliding/SlidingPlayService";
import { getJigsawPlayBoardPath, getJigsawStoryTitle, jigsawPlayRoToCardPuzzle } from "../puzzles/jigsaw/jigsawBrowseUtils";
import { slidingPlayRoToCardPuzzle } from "../browse-sliding/slidingBrowseUtils";
import Logger from "../../services/Logger";
import { SURFACE } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";

const TYPE_LABEL = {
    JIGSAW: "nav.jigsaw",
    SLIDING: "nav.sliding",
    CROSSWORD: "nav.crossword",
    HIDDEN_MESSAGE: "nav.hidden-msg",
    WORD_SEARCH: "nav.word-search",
};

function toMs(value) {
    if (value == null) return 0;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const n = new Date(value).getTime();
    return Number.isFinite(n) ? n : 0;
}

function playUpdatedAt(puzzle) {
    return toMs(
        puzzle?.slidingPlay?.updatedAt ||
            puzzle?.jigsawPlay?.updatedAt ||
            puzzle?.hwPlay?.updatedAt ||
            puzzle?.updatedAt ||
            puzzle?.publishedAt,
    );
}

function continueHref(puzzle) {
    const type = String(puzzle?.type || "").toUpperCase();
    const id = puzzle?.id;
    if (!id) return "/account/library";
    if (type === "JIGSAW") return getJigsawPlayBoardPath(id, puzzle.jigsawId);
    if (type === "SLIDING") return `/puzzle/play-sliding/${encodeURIComponent(id)}`;
    if (type === "CROSSWORD") return `/puzzle/play-cross/${encodeURIComponent(id)}`;
    if (type === "HIDDEN_MESSAGE") return `/puzzle/play-hidden-message/${encodeURIComponent(id)}`;
    if (type === "WORD_SEARCH") return `/puzzle/play-word-search/${encodeURIComponent(id)}`;
    return "/account/library";
}

function displayTitle(puzzle) {
    const fromStory = getJigsawStoryTitle(puzzle?.story);
    return fromStory || puzzle?.title || puzzle?.name || puzzle?.id || "";
}

function collectTags(puzzle) {
    const raw = puzzle?.tags;
    if (!Array.isArray(raw)) return [];
    return raw.map((tag) => (typeof tag === "string" ? tag : tag?.name || tag?.tag || "")).filter(Boolean);
}

function typeBrowsePath(type) {
    if (type === "JIGSAW") return "/puzzles/jigsaw";
    if (type === "SLIDING") return "/puzzles/sliding";
    if (type === "HIDDEN_MESSAGE") return "/puzzles/hidden-message";
    if (type === "WORD_SEARCH") return "/puzzles/word-search";
    return "/puzzles/crossword";
}

async function fetchMapped(fetcher) {
    try {
        const rows = await fetcher();
        return Array.isArray(rows) ? rows.filter(Boolean) : [];
    } catch (e) {
        Logger.error("Dashboard continue fetch:", e);
        return [];
    }
}

export default function DashboardContinueRow() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [moreTags, setMoreTags] = useState([]);
    const [lastType, setLastType] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const [jigsawIn, slidingIn, crossIn, hiddenIn, searchIn, jigsawDone, slidingDone, crossDone] =
                await Promise.all([
                    fetchMapped(async () => {
                        const result = await JigsawPlayService.list({
                            page: 0,
                            pageSize: 4,
                            sort: "updatedAt:desc",
                            isCompleted: false,
                        });
                        return result.success
                            ? (result.data.content || []).map(jigsawPlayRoToCardPuzzle)
                            : [];
                    }),
                    fetchMapped(async () => {
                        const response = await SlidingPlayService.listSlidingPlay({
                            page: 0,
                            pageSize: 4,
                            sort: "updatedAt:desc",
                            isCompleted: false,
                        });
                        return (response.content || []).map(slidingPlayRoToCardPuzzle);
                    }),
                    fetchMapped(async () => {
                        const response = await CrossService.requestPlayablePuzzles(
                            "CROSSWORD",
                            0,
                            4,
                            false,
                            false,
                            undefined,
                            "all",
                            false,
                        );
                        return (response.content || []).map((p) => ({ ...p, type: p.type || "CROSSWORD" }));
                    }),
                    fetchMapped(async () => {
                        const response = await GridService.requestPlayablePuzzles(
                            "HIDDEN_MESSAGE",
                            0,
                            4,
                            false,
                            false,
                            undefined,
                            "all",
                            false,
                        );
                        return (response.content || []).map((p) => ({ ...p, type: p.type || "HIDDEN_MESSAGE" }));
                    }),
                    fetchMapped(async () => {
                        const response = await GridService.requestPlayablePuzzles(
                            "WORD_SEARCH",
                            0,
                            4,
                            false,
                            false,
                            undefined,
                            "all",
                            false,
                        );
                        return (response.content || []).map((p) => ({ ...p, type: p.type || "WORD_SEARCH" }));
                    }),
                    fetchMapped(async () => {
                        const result = await JigsawPlayService.list({
                            page: 0,
                            pageSize: 2,
                            sort: "updatedAt:desc",
                            isCompleted: true,
                        });
                        return result.success
                            ? (result.data.content || []).map(jigsawPlayRoToCardPuzzle)
                            : [];
                    }),
                    fetchMapped(async () => {
                        const response = await SlidingPlayService.listSlidingPlay({
                            page: 0,
                            pageSize: 2,
                            sort: "updatedAt:desc",
                            isCompleted: true,
                        });
                        return (response.content || []).map(slidingPlayRoToCardPuzzle);
                    }),
                    fetchMapped(async () => {
                        const response = await CrossService.requestPlayablePuzzles(
                            "CROSSWORD",
                            0,
                            2,
                            true,
                            true,
                            undefined,
                            "all",
                            false,
                        );
                        return (response.content || []).map((p) => ({ ...p, type: p.type || "CROSSWORD" }));
                    }),
                ]);
            if (cancelled) return;

            const inProgress = [...jigsawIn, ...slidingIn, ...crossIn, ...hiddenIn, ...searchIn]
                .sort((a, b) => playUpdatedAt(b) - playUpdatedAt(a));
            const finished = [...jigsawDone, ...slidingDone, ...crossDone]
                .sort((a, b) => playUpdatedAt(b) - playUpdatedAt(a));
            const mixed = [...inProgress];
            for (const row of finished) {
                if (mixed.length >= 5) break;
                if (mixed.some((m) => m.id === row.id && m.type === row.type)) continue;
                mixed.push({ ...row, _finished: true });
            }
            setItems(mixed.slice(0, 5));

            const last = finished[0] || inProgress[0];
            setLastType(last?.type ? String(last.type).toUpperCase() : "");
            const tags = [];
            for (const row of [...finished, ...inProgress]) {
                for (const tag of collectTags(row)) {
                    if (!tags.includes(tag)) tags.push(tag);
                }
                if (tags.length >= 8) break;
            }
            setMoreTags(tags);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("dashboard.continue")}
                </Typography>
                <Button component={RouterLink} to="/account/library" size="small" sx={{ textTransform: "none" }}>
                    {t("dashboard.see-all-library")}
                </Button>
            </Stack>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("dashboard.no-continue")}
                </Typography>
            ) : (
                <Stack spacing={1}>
                    {items.map((puzzle) => {
                        const type = String(puzzle.type || "").toUpperCase();
                        return (
                            <Paper
                                key={`${type}-${puzzle.id}-${puzzle.jigsawId || ""}`}
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    bgcolor: SURFACE.card,
                                    borderColor: SURFACE.border,
                                    cursor: "pointer",
                                }}
                                onClick={() => navigate(continueHref(puzzle))}
                            >
                                <Chip size="small" label={t(TYPE_LABEL[type] || "nav.library")} />
                                <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, minWidth: 0 }} noWrap>
                                    {displayTitle(puzzle)}
                                </Typography>
                                <Button size="small" sx={{ textTransform: "none", flexShrink: 0 }}>
                                    {t("puzzle.card.continue")}
                                </Button>
                            </Paper>
                        );
                    })}
                </Stack>
            )}
            {!loading && (moreTags.length > 0 || lastType) ? (
                <Box sx={{ mt: 10 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {t("dashboard.more-like")}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {lastType ? (
                            <Chip
                                clickable
                                color="primary"
                                variant="outlined"
                                label={t(TYPE_LABEL[lastType] || lastType)}
                                onClick={() => navigate(typeBrowsePath(lastType))}
                            />
                        ) : null}
                        {moreTags.map((tag) => (
                            <Chip
                                key={tag}
                                clickable
                                variant="outlined"
                                label={tag}
                                onClick={() => navigate("/puzzles/crossword")}
                            />
                        ))}
                    </Stack>
                </Box>
            ) : null}
        </Box>
    );
}
